import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Panel } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Loader2, Save } from "lucide-react";

const SECTIONS = [
  { title: "About your school", fields: [
    ["owner_name", "Owner name", "text"], ["business_name", "School / gym name", "text"],
    ["business_type", "Business type", "text"], ["styles", "Styles / programs", "text"],
    ["location", "Location", "text"], ["website", "Website", "text"], ["crm", "CRM / software", "text"],
  ]},
  { title: "Your numbers", fields: [
    ["members", "Active members", "number"], ["avg_price", "Avg monthly price ($)", "number"],
    ["trial_price", "Paid trial price ($)", "number"],
    ["monthly_revenue", "Monthly revenue ($)", "number"], ["ad_spend", "Monthly ad spend ($)", "number"],
    ["leads", "Leads / month", "number"], ["appts_booked", "Appointments booked", "number"],
    ["appts_showed", "Appointments showed", "number"], ["enrollments", "Enrollments / month", "number"],
    ["cancellations", "Cancellations / month", "number"],
  ]},
  { title: "Goals & problems", fields: [
    ["primary_goal", "Primary goal", "text"], ["biggest_problem", "Biggest problem", "textarea"],
  ]},
];

export default function School() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/profile").then((r) => { setData(r.data || {}); setLoading(false); }).catch(() => setLoading(false)); }, []);
  const set = (k, type) => (e) => setData((d) => ({ ...d, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...data }; delete clean.id; delete clean.business_id;
      await api.put("/profile", { data: clean });
      toast.success("Profile saved. Your coach is now up to date.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div>;

  return (
    <div className="mm-fade-in">
      <PageHeader icon={Building2} title="My School" subtitle="Your business profile — powers every diagnosis and recommendation."
        action={<Button data-testid="school-save-btn" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}</Button>} />

      <div className="space-y-6">
        {SECTIONS.map((sec) => (
          <Panel key={sec.title}>
            <div className="text-sm font-medium text-slate-300 mb-4">{sec.title}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map(([k, label, type]) => (
                <div key={k} className={`space-y-1.5 ${type === "textarea" ? "sm:col-span-2" : ""}`}>
                  <Label className="text-xs">{label}</Label>
                  {type === "textarea" ? (
                    <Textarea data-testid={`school-${k}`} value={data[k] ?? ""} onChange={set(k, type)} className="bg-slate-950 border-slate-700 min-h-20" />
                  ) : (
                    <Input data-testid={`school-${k}`} type={type} value={data[k] ?? ""} onChange={set(k, type)} className="bg-slate-950 border-slate-700 h-10" />
                  )}
                </div>
              ))}
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
